import { Banknote, CheckCheck, Clock, LifeBuoy } from "lucide-react";

import { ColocaKpiCard } from "@/shared/components/data-display/ColocaKpiCard";
import useTranslations from "@/shared/lang/useTranslations";

import type { RescueKpis } from "../types/payoutRescue.types";
import { formatMoney } from "../utils/payoutRescueFormat";

export interface RescueKpiStripProps {
  kpis: RescueKpis;
}

/**
 * KPI strip of the rescue queue. "Monto retenido" sums the GROSS of stuck-CREATED payouts (that is what the
 * wallet actually blocks on create), split by currency — the empty state ("—") means no funds are held, which
 * is the norm for an exception queue.
 */
export function RescueKpiStrip({ kpis }: RescueKpiStripProps) {
  const { t, activeLanguage } = useTranslations("payoutRescue");

  const retainedEntries = Object.entries(kpis.retainedByCurrency).sort(([a], [b]) => a.localeCompare(b));
  const retainedValue =
    retainedEntries.length === 0 ? (
      <span className="text-muted-foreground">—</span>
    ) : (
      <span className="flex flex-col gap-0.5">
        {retainedEntries.map(([currency, amount]) => (
          <span key={currency} className="whitespace-nowrap font-mono text-lg tabular-nums">
            {formatMoney(amount, activeLanguage)} {currency}
          </span>
        ))}
      </span>
    );

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <ColocaKpiCard
        label={t("kpi_pending_label")}
        value={kpis.pending}
        icon={LifeBuoy}
        accent={kpis.pending > 0 ? "warning" : "neutral"}
        meta={kpis.pending > 0 ? <span className="text-warning">{t("kpi_pending_caption")}</span> : undefined}
      />
      <ColocaKpiCard
        label={t("kpi_processing_label")}
        value={kpis.processing}
        icon={Clock}
        accent="info"
        meta={t("kpi_processing_caption")}
      />
      <ColocaKpiCard
        label={t("kpi_paid_today_label")}
        value={kpis.paidToday ?? "—"}
        icon={CheckCheck}
        accent="success"
        meta={t("kpi_paid_today_caption")}
      />
      <ColocaKpiCard label={t("kpi_retained_label")} value={retainedValue} icon={Banknote} accent="neutral" />
    </div>
  );
}
